import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatArchiveDate } from './BriefingDateNav';

const SUMMARY_MAX = 110;

const truncateSummary = (text) => {
  const trimmed = String(text || '').trim();
  if (!trimmed) return '';
  return trimmed.length > SUMMARY_MAX
    ? `${trimmed.slice(0, SUMMARY_MAX).trimEnd()}…`
    : trimmed;
};

const themeCountLabel = (count) => {
  const n = Number(count) || 0;
  return n === 1 ? '1 theme' : `${n} themes`;
};

const EntryBody = ({ entry }) => (
  <>
    <div className="flex items-center justify-between gap-2">
      <span className="font-medium text-heading">{formatArchiveDate(entry.briefing_date)}</span>
      <span className="badge-fun-sky text-xs flex-shrink-0">{themeCountLabel(entry.theme_count)}</span>
    </div>
    {entry.summary && (
      <p className="mt-1 text-xs text-secondary-text leading-snug">{truncateSummary(entry.summary)}</p>
    )}
  </>
);

const BriefingDatePicker = ({ dates, displayedDate }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const entries = dates || [];

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Browse briefing archive"
        title="Browse briefing archive"
        className="btn-clay-ghost p-2"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3M4 11h16M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z" />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Archived briefings"
          className="absolute right-0 mt-2 w-72 sm:w-80 max-h-96 overflow-y-auto card-clay-static !p-0 z-30"
        >
          {entries.length === 0 ? (
            <div className="p-4 text-center">
              <div className="text-3xl mb-2">🗓️</div>
              <p className="font-medium text-heading">No archived briefings yet</p>
              <p className="mt-1 text-xs text-secondary-text">
                Today&apos;s briefing moves into the archive tomorrow.
              </p>
            </div>
          ) : (
            entries.map((entry) => {
              // Reason: the live view serves the most recent *completed* briefing
              // regardless of date, so the date on screen is often one the index
              // also lists. Offering it as a destination would navigate to a
              // read-only copy of the page the user is already reading.
              const isCurrent = entry.briefing_date === displayedDate;
              return isCurrent ? (
                <div
                  key={entry.briefing_date}
                  role="option"
                  aria-selected="true"
                  className="block w-full text-left px-4 py-3 border-b-2 border-clay-border last:border-b-0 bg-surface-hover"
                >
                  <EntryBody entry={entry} />
                  <span className="mt-1 inline-block text-xs font-medium text-tertiary-text">
                    Currently viewing
                  </span>
                </div>
              ) : (
                <Link
                  key={entry.briefing_date}
                  to={`/daily/${entry.briefing_date}`}
                  role="option"
                  aria-selected="false"
                  onClick={() => setOpen(false)}
                  className="block w-full text-left px-4 py-3 border-b-2 border-clay-border last:border-b-0 hover:bg-surface-hover transition-colors no-underline"
                >
                  <EntryBody entry={entry} />
                </Link>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default BriefingDatePicker;
